// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * One point returned by Enedis. Address fields are the AFNOR normalized address lines of the answer (adresseInstallationNormalisee), under the names BOV1 /pdl/search gives them, so that a front displays the point the same way here and there.
 *
 */
export const zEnedisSearchPoint = z.object({
    deliveryPoint: z.string(),
    contractOwner: z.string().optional(),
    recipient: z.string().nullish(),
    addressComplementLevel: z.string().nullish(),
    addressComplementBuilding: z.string().nullish(),
    address: z.string().optional(),
    locality: z.string().nullish(),
    zipCode: z.string(),
    country: z.string().nullish(),
    matricule: z.string().nullish(),
    serialNumber: z.string().optional(),
    meterTypeCode: z.string().optional(),
    meterTypeLabel: z.string().optional(),
    contractualStatusCode: z.string(),
    contractualStatusLabel: z.string().optional()
});

/**
 * Delivery point identifier (PDL) operated by Enedis: exactly 14 characters, the last two being a control key computed on characters 4 to 12. The control key cannot be expressed in JSON Schema and is checked by the domain, which answers INVALID_ENEDIS_PDL_SIZE or INVALID_ENEDIS_CONTROL_KEY. Surrounding whitespace is trimmed before that check, but is not accepted here: send the 14 characters as is.
 *
 */
export const zEnedisDeliveryPoint = z.string().length(14);

export const zDomainErrorResponse = z.object({
    errorCategory: z.enum(['DOMAIN_ERROR']),
    errorDetails: z.object({
        errorType: z.string().nullable(),
        errorCode: z.string().nullable(),
        message: z.string().nullable()
    })
});

export const zTechnicalErrorResponse = z.object({
    errorCategory: z.enum(['TECHNICAL_ERROR']),
    errorDetails: z.object({
        errorType: z.string().nullable(),
        errorCode: z.string().nullish(),
        message: z.string().nullable()
    })
});

export const zFieldError = z.object({
    errorCode: z.string(),
    message: z.string(),
    propertyPath: z.string()
});

export const zFormValidationErrorResponse = z.object({
    errorCategory: z.enum(['FORM_VALIDATION_ERROR']),
    errorDetails: z.object({
        fieldErrors: z.array(zFieldError)
    })
});

export const zUnexpectedErrorResponse = z.object({
    errorCategory: z.enum(['UNEXPECTED_ERROR']),
    errorDetails: z.object({
        errorType: z.string(),
        message: z.string()
    })
});

export const zErrorResponse = z.union([
    z.object({
        errorCategory: z.literal('DOMAIN_ERROR')
    }).and(zDomainErrorResponse),
    z.object({
        errorCategory: z.literal('TECHNICAL_ERROR')
    }).and(zTechnicalErrorResponse),
    z.object({
        errorCategory: z.literal('FORM_VALIDATION_ERROR')
    }).and(zFormValidationErrorResponse),
    z.object({
        errorCategory: z.literal('UNEXPECTED_ERROR')
    }).and(zUnexpectedErrorResponse)
]);

/**
 * Unique identifier for a site
 */
export const zSiteId = z.number().int();

export const zEnedisSearchRequestView = z.object({
    id: z.number().int(),
    siteId: zSiteId.nullable(),
    status: z.enum([
        'ONE_RESULT',
        'MULTIPLE_RESULTS',
        'NO_RESULT'
    ]),
    queryLevel: z.enum([
        'REMOVE_SPECIAL_CHARACTERS',
        'KEEP_ONLY_THREE_LETTERS_NAME',
        'REMOVE_NUMBER_EXTENSIONS_ADDRESS',
        'KEEP_ONLY_THREE_LETTERS_CONTRACT_AND_REMOVE_NUMBER_EXTENSIONS_ADDRESS'
    ]),
    points: z.array(zEnedisSearchPoint)
});

/**
 * Name of the owner of the Enedis contract
 */
export const zEnedisContractOwner = z.string().min(3).max(255);

/**
 * One AFNOR address line as returned by Enedis
 */
export const zEnedisAddressElement = z.string().min(1).max(255);

export const zCreateEnedisSearchRequest = z.object({
    siteId: zSiteId,
    contractOwner: zEnedisContractOwner,
    streetNumberAndName: zEnedisAddressElement
});

export const zSiteBusinessSegment = z.enum([
    'ORGANIZATION',
    'HOUSEHOLDER',
    'UNKNOWN'
]);

/**
 * NOT_VALIDATED is also reported when no Enedis delivery point information exists at all, i.e. the site was created without any validation.
 *
 */
export const zPdlValidationStatus = z.enum([
    'NOT_VALIDATED',
    'VALIDATED',
    'AUTO_VALIDATION_FAILED',
    'OVERRIDDEN',
    'INVALIDATED'
]);

export const zSiteEnedisPdlInfo = z.object({
    validationStatus: zPdlValidationStatus
});

/**
 * Delivery point identifier (PDL), operated by Enedis or by any other DSO
 */
export const zDeliveryPoint = z.string().min(1).max(255);

export const zSiteElectricMeter = z.object({
    id: z.number().int(),
    operatedByEnedis: z.boolean(),
    dsoId: z.number().int().nullable(),
    dsoName: z.string().nullable(),
    deliveryPoint: zDeliveryPoint.nullable(),
    enedisPdlInfo: zSiteEnedisPdlInfo.nullable()
});

export const zSiteElectricMeters = z.object({
    meters: z.array(zSiteElectricMeter),
    siteBusinessSegment: zSiteBusinessSegment
});

export const zNullableEnedisAddressElement = zEnedisAddressElement.nullable();

/**
 * One element of an Enedis delivery point search result, sent back as is by the front, address lines named as the search endpoint hands them over. Only the delivery point and the address lines are used, the meter and contract fields are accepted but not stored yet.
 *
 */
export const zEnedisSearchResult = z.object({
    deliveryPoint: zEnedisDeliveryPoint,
    contractOwner: zEnedisContractOwner,
    recipient: zNullableEnedisAddressElement.optional(),
    addressComplementLevel: zNullableEnedisAddressElement.optional(),
    addressComplementBuilding: zNullableEnedisAddressElement.optional(),
    address: zNullableEnedisAddressElement.optional(),
    locality: zNullableEnedisAddressElement.optional(),
    zipCode: zEnedisAddressElement,
    country: zNullableEnedisAddressElement.optional(),
    matricule: z.string().nullish(),
    serialNumber: z.string().nullish(),
    meterTypeCode: z.string().nullish(),
    meterTypeLabel: z.string().nullish(),
    contractualStatusCode: z.string().nullish(),
    contractualStatusLabel: z.string().nullish()
});

export const zValidatePdlFromSearchRequest = z.object({
    enedisSearchResult: zEnedisSearchResult
});

export const zRegisterDeliveryPointRequest = z.object({
    deliveryPoint: zDeliveryPoint
});

/**
 * Unique identifier for a subscription pipeline
 */
export const zSubscriptionPipelineId = z.number().int();

/**
 * Unique identifier for a subscription failure category
 */
export const zSubscriptionFailureCategoryId = z.number().int();

/**
 * Unique code for a subscription failure category
 */
export const zSubscriptionFailureCategoryCode = z.string().min(1).max(50);

/**
 * Unique identifier for a subscription failure reason
 */
export const zSubscriptionFailureReasonId = z.number().int();

/**
 * Unique code for a subscription failure reason
 */
export const zSubscriptionFailureReasonCode = z.string().min(1).max(50);

/**
 * A failure reason within a category
 */
export const zFailureReason = z.object({
    id: zSubscriptionFailureReasonId,
    name: z.string(),
    code: zSubscriptionFailureReasonCode,
    comment: z.string().nullable()
});

/**
 * A failure category containing reasons
 */
export const zFailureCategory = z.object({
    id: zSubscriptionFailureCategoryId,
    name: z.string(),
    code: zSubscriptionFailureCategoryCode,
    comment: z.string().nullable(),
    reasons: z.array(zFailureReason)
});

export const zFailureReferential = z.object({
    PHONING_DELAYED_KO: z.array(zFailureCategory),
    PHONING_IMMEDIATE_KO: z.array(zFailureCategory)
});

/**
 * Failure categories and reasons for a subscription pipeline
 */
export const zSubscriptionPipelineFailures = z.object({
    pipelineId: zSubscriptionPipelineId,
    referential: zFailureReferential
});

/**
 * Unique identifier for a subscription pipeline tag
 */
export const zSubscriptionPipelineTagId = z.number().int();

/**
 * A subscription tag path made of up to four parts joined by the '::' separator
 */
export const zSubscriptionTagPath = z.string().min(1).max(206);

/**
 * A tag available for subscriptions in the pipeline
 */
export const zSubscriptionPipelineTag: z.AnyZodObject = z.object({
    id: zSubscriptionPipelineTagId,
    parentId: zSubscriptionPipelineTagId.nullable(),
    value: z.string().min(1).max(50),
    path: zSubscriptionTagPath,
    children: z.array(z.lazy(() => zSubscriptionPipelineTag))
});

export const zCreateDirectSubscriptionRequest = z.object({
    siteId: zSiteId,
    tags: z.array(zSubscriptionTagPath)
});

export const zAddSubscriptionTagRequest = z.object({
    tags: z.array(zSubscriptionTagPath).min(1)
});

export const zRemoveSubscriptionTagRequest = z.object({
    tags: z.array(zSubscriptionTagPath).min(1)
});

/**
 * Unique identifier for a subscription
 */
export const zSubscriptionId = z.number().int();

/**
 * Current status of the subscription
 */
export const zSubscriptionStatus = z.enum([
    'RUNNING',
    'COMPLETED',
    'FAILED'
]);

/**
 * Status of the installation process
 */
export const zInstallationStatus = z.enum([
    'REQUIRED',
    'FAILED',
    'COMPLETED',
    'NOT_REQUIRED',
    'NEW_INTERVENTION_REQUIRED'
]);

/**
 * Status of the prospection process
 */
export const zProspectionStatus = z.enum([
    'REQUIRED',
    'NOT_REQUIRED',
    'COMPLETED'
]);

/**
 * Outcome of the prospection process
 */
export const zProspectionOutcome = z.enum([
    'WITH_PLANNED_APPOINTMENT',
    'WITH_SUBSCRIBER_ON_HOLD',
    'WITHOUT_APPOINTMENT'
]);

/**
 * Status of eligibility confirmation
 */
export const zEligibilityConfirmationStatus = z.enum([
    'NOT_REQUIRED',
    'REQUIRED',
    'ELIGIBILITY_CONFIRMED'
]);

/**
 * Outcome of the eligibility confirmation process
 */
export const zEligibilityConfirmationOutcome = z.enum([
    'WITH_PLANNED_APPOINTMENT',
    'WITH_SUBSCRIBER_ON_HOLD',
    'WITHOUT_APPOINTMENT'
]);

/**
 * Complete subscription status information
 */
export const zSubscriptionViewStatuses = z.object({
    status: zSubscriptionStatus,
    installationStatus: zInstallationStatus,
    prospectionStatus: zProspectionStatus,
    prospectionOutcome: zProspectionOutcome.nullish(),
    prospectionOutcomePlannedAppointmentId: z.number().int().nullish(),
    eligibilityConfirmationStatus: zEligibilityConfirmationStatus,
    eligibilityConfirmationOutcome: zEligibilityConfirmationOutcome.nullish(),
    eligibilityConfirmationPlannedAppointmentId: z.number().int().nullish()
});

/**
 * Information after status change operations.
 */
export const zSubscriptionStatusTransitionResult = z.object({
    statuses: zSubscriptionViewStatuses
});

/**
 * Unique identifier for a subscriber
 */
export const zSubscriberId = z.number().int();

/**
 * Person's first name
 */
export const zFirstName = z.string().min(1).max(150);

/**
 * Person's last name
 */
export const zLastName = z.string().min(1).max(150);

export const zSubscriptionViewSubscriber = z.object({
    id: zSubscriberId,
    firstName: zFirstName,
    lastName: zLastName
});

/**
 * Unique identifier for a legal entity (partner)
 */
export const zLegalEntityId = z.number().int();

export const zSubscriptionViewPartner = z.object({
    id: zLegalEntityId,
    note: z.string()
});

/**
 * Channel through which subscription was acquired
 */
export const zAcquisitionChannel = z.enum([
    'DIGITAL_LEAD',
    'FULL_DIGITAL_SUBSCRIPTION',
    'DIRECT_ACQUISITION',
    'EXTERNAL_VENDOR',
    'LANDLORD'
]);

/**
 * ISO 8601 local date-time without timezone (LocalDateTime)
 */
export const zDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/);

export const zSubscriptionViewOnHoldPeriod = z.object({
    id: z.number().int(),
    start: zDateTime,
    end: zDateTime,
    operatorUsername: z.string(),
    operatorId: z.number().int()
});

/**
 * Unique identifier for a subscription tag
 */
export const zSubscriptionTagId = z.number().int();

export const zSubscriptionViewTag = z.object({
    id: zSubscriptionTagId,
    value: z.string(),
    path: zSubscriptionTagPath.optional()
});

/**
 * Processing result of a delayed closure request
 */
export const zDelayedClosureProcessingResult = z.enum([
    'NOT_CLOSABLE',
    'DUPLICATE',
    'CLOSABLE_AFTER_DELAY',
    'CLOSABLE_IMMEDIATELY'
]);

export const zSubscriptionViewDelayedClosureRequest = z.object({
    id: z.number().int(),
    startExecutionDateTime: zDateTime,
    scheduledClosureDateTime: zDateTime,
    processingResult: zDelayedClosureProcessingResult.nullable(),
    creationDatetime: zDateTime,
    origin: z.string(),
    operatorUsername: z.string(),
    operatorId: z.number().int(),
    failureReasonCode: zSubscriptionFailureReasonCode,
    failureReasonLabel: z.string()
});

export const zComment = z.string().max(1000);

export const zSubscriptionViewFailure = z.object({
    reason: zSubscriptionFailureReasonCode,
    category: zSubscriptionFailureCategoryCode,
    reasonLabel: z.string(),
    categoryLabel: z.string(),
    operatorUsername: z.string(),
    operatorId: z.number().int(),
    comment: zComment.nullable()
});

/**
 * Action identifier for subscription status transitions
 */
export const zStatusTransitionAction = z.enum([
    'COMPLETE_PROSPECTION',
    'CONFIRM_ELIGIBILITY',
    'COMPLETE_INSTALLATION',
    'INSTALLATION_REQUIRE_NEW_INTERVENTION',
    'FAIL_SUBSCRIPTION_IMMEDIATELY',
    'REOPEN_SUBSCRIPTION'
]);

/**
 * Scope grouping subscription status transition actions
 */
export const zScope = z.enum([
    'PROSPECTION',
    'ELIGIBILITY_CONFIRMATION',
    'INSTALLATION',
    'GLOBAL'
]);

export const zSubscriptionAvailableAction = z.object({
    action: zStatusTransitionAction,
    available: z.boolean(),
    scopes: z.array(zScope).min(1)
});

/**
 * Detailed subscription view
 */
export const zSubscriptionView = z.object({
    id: zSubscriptionId,
    pipelineId: zSubscriptionPipelineId,
    statuses: zSubscriptionViewStatuses,
    subscriber: zSubscriptionViewSubscriber,
    siteId: zSiteId,
    partner: zSubscriptionViewPartner,
    acquisitionChannel: zAcquisitionChannel,
    creationDatetime: zDateTime,
    closureDatetime: zDateTime.nullable(),
    tags: z.array(zSubscriptionViewTag),
    onHoldPeriods: z.array(zSubscriptionViewOnHoldPeriod),
    delayedClosureRequests: z.array(zSubscriptionViewDelayedClosureRequest),
    failure: zSubscriptionViewFailure.nullish(),
    actions: z.array(zSubscriptionAvailableAction),
    subscriptionClosedAsConflict: zSubscriptionId.nullable()
});

export const zDataTableSort = z.object({
    fieldName: z.string(),
    direction: z.enum(['ASC', 'DESC'])
});

export const zPaginatedDataTableQueryRequest = z.object({
    page: z.number().int().gte(0),
    pageSize: z.number().int().gte(1).lte(100),
    sorts: z.array(zDataTableSort)
});

export const zBaseDataTableFilter = z.object({
    operator: z.enum([
        'EQUALS',
        'NOT_EQUALS',
        'CONTAINS',
        'CONTAINS_IGNORE_CASE',
        'GREATER_THAN',
        'LESS_THAN',
        'IN'
    ]),
    value: z.unknown()
});

export const zDataTableResultResponse = z.object({
    totalRows: z.number().int(),
    invalidFilters: z.array(z.object({
        name: z.string(),
        code: z.enum([
            'UNKNOWN_COLUMN',
            'INVALID_VALUE',
            'UNEXPECTED_FILTER_ERROR'
        ]),
        message: z.string().nullish()
    }))
});

/**
 * Unique identifier for an operator
 */
export const zOperatorId = z.number().int();

export const zSubscriptionDataTablePersonDeclaringFailure = z.object({
    id: zOperatorId,
    firstname: zFirstName,
    lastname: zLastName,
    login: z.string()
});

export const zSubscriptionDataTableFailure = z.object({
    reasonCode: zSubscriptionFailureReasonCode,
    reasonLabel: z.string(),
    comment: zComment.nullish(),
    personDeclaringFailure: zSubscriptionDataTablePersonDeclaringFailure.nullish()
});

export const zSubscriptionDataTableResponse = zDataTableResultResponse.and(z.object({
    rows: z.array(z.object({
        id: zSubscriptionId,
        subscriberFirstName: zFirstName,
        subscriberLastName: zLastName,
        status: zSubscriptionStatus,
        pipelineId: zSubscriptionPipelineId,
        creationDatetime: zDateTime,
        closureDateTime: zDateTime.nullish(),
        subscriptionFailure: zSubscriptionDataTableFailure.nullish()
    })).optional()
}));

/**
 * Delivery point identifier (PDL) of the Enedis delivery point information
 */
export const zEnedisDeliveryPoint2 = zEnedisDeliveryPoint;

/**
 * Unique identifier for a site
 */
export const zSiteId2 = zSiteId;

/**
 * Unique identifier for a subscription
 */
export const zSubscriptionId2 = zSubscriptionId;

/**
 * Outcome of the search: ONE_RESULT or MULTIPLE_RESULTS with the points of the last played level, NO_RESULT when the whole degradation chain came back empty, FAILED with the raw SGE error code or a technical detail.
 *
 */
export const zSearchEnedisPointsResponse = zEnedisSearchRequestView;

/**
 * Electric meters of the site
 */
export const zGetSiteElectricMetersResponse = zSiteElectricMeters;

/**
 * Pipeline failure categories retrieved successfully
 */
export const zGetPipelineFailuresResponse = zSubscriptionPipelineFailures;

/**
 * Pipeline tags retrieved successfully
 */
export const zGetPipelineTagsResponse = z.array(zSubscriptionPipelineTag);

/**
 * Subscription created successfully
 */
export const zCreateDirectSubscriptionResponse = zSubscriptionView;

export const zGetActiveSubscriptionResponse = z.union([
    zSubscriptionView,
    z.void()
]);

export const zGetLatestSubscriptionResponse = z.union([
    zSubscriptionView,
    z.void()
]);

/**
 * Search results retrieved successfully
 */
export const zSearchSiteSubscriptionsHistoryResponse = zSubscriptionDataTableResponse;

/**
 * Subscription re-opened successfully
 */
export const zReOpenSubscriptionResponse = zSubscriptionStatusTransitionResult;
